import { SxProps, Typography, TypographyProps } from "@mui/material";
import { Grid } from "@mui/material";
import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import theme from "Theme/AppTheme";
import { sxClsx } from "utils/styleClsx";

interface AcxExpandableTextProps {
    maxTruncatedLines?: number;
    pendoIgnore?: boolean;
    defaultExpanded?: boolean;
    containerClassName?: string;
    containerSx?: SxProps;
}

export function AcxExpandableText({
    maxTruncatedLines = 3,
    defaultExpanded = false,
    pendoIgnore,
    containerClassName,
    containerSx,
    children,
    component,
    ...props
}: TypographyProps & React.PropsWithChildren<AcxExpandableTextProps>) {
    const textRef = useRef<React.ComponentRef<typeof Typography>>(null);
    const [isClamped, setIsClamped] = useState(true);

    useEffect(() => {
        function checkIsClamped() {
            if (!textRef.current) return;

            setIsClamped(
                textRef.current.clientHeight !== textRef.current.scrollHeight,
            );
        }

        checkIsClamped();

        window.addEventListener("resize", checkIsClamped);
        return () => window.removeEventListener("resize", checkIsClamped);
        // `textRef` is stable across renders; re-check whenever the text itself can have changed,
        // since content that grows after mount (e.g. loaded asynchronously) can newly start clamping.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [textRef, children]);

    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    return (
        <Grid
            container
            direction="column"
            gap={1}
            className={containerClassName}
            sx={sxClsx(containerSx)}
        >
            <Grid item>
                <Typography
                    {...props}
                    component={component ?? "div"}
                    ref={textRef}
                    className={clsx(pendoIgnore && "pendo-ignore")}
                    sx={sxClsx(
                        {
                            lineHeight: "normal",
                        },
                        !isExpanded && {
                            display: "-webkit-box",
                            WebkitLineClamp: `${maxTruncatedLines}`,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        },
                    )}
                >
                    {children}
                </Typography>
            </Grid>
            {isClamped && (
                <Grid item>
                    <Typography
                        sx={{
                            cursor: "pointer",
                            color: "#3564D5",
                            userSelect: "none",
                        }}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            setIsExpanded((expanded) => !expanded);
                        }}
                        color={theme.palette.primary.main}
                    >
                        {isExpanded ? "Show Less" : "Show More"}
                    </Typography>
                </Grid>
            )}
        </Grid>
    );
}
